import {
    FetchState,
    ListParams
} from "@/shared/types";
import {
    UserSearchResultList,
    UserSearchResultModel,
    UserSearchState
} from "../types";
import * as storeHelper from "@/shared/storeHelper";
import {
    getUserById,
    loadUsers
} from "@/shared/api/userService";
import { AxiosError } from "axios";
import _ from "lodash";
import moment from "moment";
import Vue from "vue";
import { defineStore } from "pinia";
import { usePageSizeStore } from "@/shared/store/pageSize";

export const initialState: UserSearchState = {
    fetchState: FetchState.Uninitialized,
    pageNumber: 1,
    totalRecords: 0,
    maxScore: 0,
    elapsedMilliseconds: 0,
    users: [],
    userPages: []
};

export const useUserSearchStore = defineStore("userSearch", {
    state: (): UserSearchState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isLoading: state => state.fetchState === FetchState.Loading,
        pageCount () {
            return this.totalRecords < this.pageSize
                ? 1
                : Math.floor((this.totalRecords + this.pageSize - 1) / this.pageSize);
        },
        pageSize () {
            const pageSizeStore = usePageSizeStore();
            return pageSizeStore.pageSize;
        },
        getUsersByPageNumber: state => (pageNumber: number): UserSearchResultModel[] => {
            const pageObject = state.userPages[pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }

            return pageObject;
        },
        userDetail: state => (userId: number): UserSearchResultModel => {
            return state.users.find(u => u.userId === userId);
        }
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setUsers (userList: UserSearchResultList) {
            for (const user of userList.users) {
                if (user.dateOfBirth) {
                    user.dateOfBirth = moment(user.dateOfBirth).format("MM/DD/YYYY");
                }
            }

            const userPages = [ ...this.userPages ];
            userPages[userList.pageNumber] = userList.users;

            this.totalRecords = userList.totalRecords;
            this.maxScore = userList.maxScore;
            this.elapsedMilliseconds = userList.elapsedMilliseconds;
            this.pageNumber = userList.pageNumber;
            this.userPages = userPages;
            this.users = this.users.concat(userList.users);
        },
        clearUserPages () {
            this.pageNumber = 1;
            this.totalRecords = 0;
            this.elapsedMilliseconds = 0;
            this.maxScore = 0;
            this.users = [];
            this.userPages = [];
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setUser (user: UserSearchResultModel) {
            const index = this.users.findIndex(u => u.userId === user.userId);

            if (index >= 0) {
                Vue.set(this.users, index, user);
            } else {
                this.users = [ user, ...this.users ];
            }
        },
        loadUser (userId: number) {
            const existingUser = _.find(this.users, u => u.userId === userId);

            if (existingUser) {
                return Promise.resolve(existingUser);
            }

            return getUserById(userId)
                .then(response => {
                    this.setUser(response.data);
                    return Promise.resolve(response.data);
                })
                .catch((error: AxiosError | any) => {
                    const displayError = error.response.status === 404 ? `User with id ${userId} not found` : error;
                    storeHelper.handleError({ error: displayError, rethrow: false });
                    return Promise.reject();
                });
        },
        loadUsers ({ clearState, pageNumber, searchTerm }: ListParams) {
            if (this.fetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            if (clearState) {
                this.clearUserPages();
                this.setFetchState(FetchState.Loading);
                return loadUsers(pageNumber, this.pageSize, searchTerm)
                    .then(response => {
                        this.setUsers(response.data);
                        this.setFetchState(FetchState.Loaded);
                    });
            }

            const pageObject = this.userPages[pageNumber || 1];
            if (typeof pageObject !== "undefined") {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);
            return loadUsers(pageNumber, this.pageSize, searchTerm)
                .then(response => {
                    this.setUsers(response.data);
                    this.setFetchState(FetchState.Loaded);
                });
        },
        reset () {
            this.clearUserPages();
            this.setFetchState(FetchState.Uninitialized);
        }
    }
});